import React from 'react';
import { motion } from 'framer-motion';
import { formatCurrency, formatNumber } from '../context/CalculatorContext';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info, HelpCircle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

export function ResultsPanel({ 
  title, 
  results, 
  showMinInvestmentWarning = false,
  minInvestmentAmount = 1500,
  isCalculating = false 
}) {
  if (!results || results.length === 0) {
    return (
      <div className="lg:sticky lg:top-24 bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-4">{title}</h3>
        <div className="text-center py-12 text-slate-400">
          <Info className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p className="text-sm">Completa los campos para ver los resultados</p>
        </div>
      </div>
    );
  }

  const mainResult = results.find(r => r.isMain);
  const secondaryResults = results.filter(r => !r.isMain);

  return (
    <motion.div 
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      className="lg:sticky lg:top-24 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
      data-testid="results-panel"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1A56DB] to-[#3B82F6] px-6 py-4">
        <h3 className="text-lg font-bold text-white">{title}</h3>
      </div>

      <div className="p-6">
        {/* Main Result */}
        {mainResult && (
          <motion.div 
            key={mainResult.value}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="mb-6 p-6 rounded-xl bg-gradient-to-br from-slate-50 to-blue-50 border border-blue-100"
          >
            <p className="text-sm text-slate-600 font-medium mb-2">{mainResult.label}</p>
            <p className="number-display text-[#1A56DB]" data-testid="main-result-value">
              {mainResult.isCurrency ? formatCurrency(mainResult.value) : formatNumber(mainResult.value)}
            </p>
            {mainResult.suffix && (
              <p className="text-sm text-slate-500 mt-1">{mainResult.suffix}</p>
            )}
          </motion.div>
        )}

        {/* Secondary Results */}
        <div className="space-y-4">
          {secondaryResults.map((result, index) => (
            <motion.div 
              key={result.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className={`p-4 rounded-xl border ${
                result.variant === 'success' ? 'bg-emerald-50 border-emerald-200' :
                result.variant === 'warning' ? 'bg-amber-50 border-amber-200' :
                result.variant === 'danger' ? 'bg-red-50 border-red-200' :
                'bg-slate-50 border-slate-200'
              }`}
              data-testid={`result-${result.id || index}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-slate-600 font-medium">{result.label}</p>
                    {result.hasTooltip && result.tooltipText && (
                      <TooltipProvider delayDuration={200}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" className="text-slate-400 hover:text-slate-600 transition-colors">
                              <HelpCircle className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent 
                            side="top" 
                            className="max-w-xs text-sm bg-slate-900 text-white p-3 rounded-lg shadow-xl"
                          >
                            {result.tooltipText}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                  <p className={`text-xl font-bold mt-1 font-mono ${
                    result.variant === 'success' ? 'text-emerald-700' :
                    result.variant === 'warning' ? 'text-amber-700' :
                    result.variant === 'danger' ? 'text-red-700' :
                    'text-slate-900'
                  }`}>
                    {result.isCurrency ? formatCurrency(result.value) : 
                     result.isPercentage ? `${result.value.toFixed(1)}%` :
                     formatNumber(result.value)}
                  </p>
                  {result.suffix && (
                    <p className="text-xs text-slate-500 mt-1">{result.suffix}</p>
                  )}
                </div>
                {result.variant === 'success' && <CheckCircle className="h-5 w-5 text-emerald-500 flex-shrink-0" />}
                {result.variant === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0" />}
                {result.variant === 'danger' && <TrendingDown className="h-5 w-5 text-red-500 flex-shrink-0" />}
                {!result.variant && <TrendingUp className="h-5 w-5 text-slate-400 flex-shrink-0" />}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Min Investment Warning */}
        {showMinInvestmentWarning && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200"
          >
            <div className="flex gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-800">Inversión muy baja</p>
                <p className="text-sm text-amber-700 mt-1">
                  Recomendamos un mínimo de {formatCurrency(minInvestmentAmount)}/mes para que el algoritmo pueda optimizar las campañas correctamente.
                </p>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
